import {
    enableProdMode,
    provideAppInitializer,
    inject,
    importProvidersFrom,
    LOCALE_ID,
    provideZoneChangeDetection
} from '@angular/core';
import { environment } from './environments/environment';
import { map, catchError, take, of } from 'rxjs';
import { provideHttpClient, withInterceptorsFromDi, HttpClient, withXhr } from '@angular/common/http';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { ReactiveFormsModule } from '@angular/forms';
import { NgxsModule } from '@ngxs/store';
import { UserState } from './app/auth/state/user.state';
import { ApplicationsState } from './app/applications/state/applications.state';
import { ApplicationProcessState } from './app/dashboard/application-process/state/application-process.state';
import { ScreenState } from './app/shared/state/screen.state';
import { DashboardOverviewState } from './app/dashboard/dashboard-overview/state/dashboard-overview.state';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { NgxsFormPluginModule } from '@ngxs/form-plugin';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { AppComponent } from './app/app.component';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { APP_ROUTES } from './app/app.routes';
import { registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import { provideMsalServices } from './app/auth/providers/provide-msal-services';
import { provideMsalConfiguration } from './app/auth/providers/provide-msal-configuration';
import { provideInterceptors } from './app/auth/providers/provide-interceptors';
import { MsalService } from '@azure/msal-angular';
import { ShuiEnvironment } from '@shui/core/utils/environment.js';
import { provideUserManagement } from '@gbr-monorepo/user-mgmt-ui';
import { GraphUserService } from './app/shared/services/graph-user.service';

registerLocaleData(localeDe);

ShuiEnvironment.setUseRedesignedIcons(true);

export function createTranslateLoader(http: HttpClient): TranslateHttpLoader {
    const fileLoc = encodeURIComponent('customer/');
    return new TranslateHttpLoader(http, `${environment.translationsUrl}/translations/${fileLoc}`, '.json');
}

// Initialize MSAL and handle redirect callbacks
function initializeMsal(msalService: MsalService) {
    return () => {
        return msalService.handleRedirectObservable().pipe(
            map((): void => {
                // Set the active account after login
                const accounts = msalService.instance.getAllAccounts();
                if (accounts.length > 0) {
                    const activeAccount = msalService.instance.getActiveAccount() || accounts[0];
                    msalService.instance.setActiveAccount(activeAccount);
                }
                return undefined;
            }),
            catchError(error => {
                console.error('Error during MSAL initialization:', error);
                return of(undefined);
            }),
            take(1)
        );
    };
}

if (environment.production) {
    enableProdMode();
}

bootstrapApplication(AppComponent, {
    providers: [
        provideZoneChangeDetection(),
        provideUserManagement(GraphUserService),
        importProvidersFrom(
            BrowserModule,
            ReactiveFormsModule,
            NgxsModule.forRoot(
                [UserState, ApplicationsState, ApplicationProcessState, ScreenState, DashboardOverviewState],
                {
                    developmentMode: !environment.production,
                    compatibility: {
                        strictContentSecurityPolicy: true
                    }
                }
            ),
            NgxsFormPluginModule.forRoot(),
            TranslateModule.forRoot({
                defaultLanguage: 'de',
                loader: {
                    provide: TranslateLoader,
                    useFactory: createTranslateLoader,
                    deps: [HttpClient]
                }
            })
        ),
        provideRouter(
            APP_ROUTES,
            withInMemoryScrolling({
                scrollPositionRestoration: 'top'
            })
        ),
        provideAppInitializer(() => {
            const initializerFn = initializeMsal(inject(MsalService));
            return initializerFn();
        }),
        { provide: LOCALE_ID, useValue: 'de-DE' },
        provideHttpClient(withXhr(), withInterceptorsFromDi()),
        provideAnimations(),
        provideMsalServices(),
        provideMsalConfiguration(),
        provideInterceptors()
    ]
}).catch(err => console.error(err));
